//src/staff/ClassStudentList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import BASE_URL from "../config";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {
    Close,
    Search,
    School,
    Save,
    Person
} from "@mui/icons-material";
export default function ClassStudentList() {
    const [assignments, setAssignments] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState("");
    const [selectedClass, setSelectedClass] = useState("");
    const [selectedSection, setSelectedSection] = useState("");
    const [students, setStudents] = useState([]);
    const [loadingAssignments, setLoadingAssignments] =
        useState(false);
    const [loadingStudents, setLoadingStudents] =
        useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedStudent, setSelectedStudent] =
        useState(null);
    const [progressOpen, setProgressOpen] =
        useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [progress, setProgress] = useState({
        attendance: "",
        homework: "",
        iq: "",
        classroomDiscipline: "",
        academicPerformance: "",
        behaviors: "",
        teachersRemark: ""
    });
    const regId =
        sessionStorage.getItem("adminRegId");
    /*
     * LOAD CLASS TEACHER ASSIGNMENTS
     */
    useEffect(() => {
        if (!regId) {
            return;
        }
        loadAssignments();
    }, [regId]);
    const loadAssignments = async () => {
        try {
            setLoadingAssignments(true);
            const response = await axios.get(
                `${BASE_URL}/api/staff-class-teacher-assignments/${regId}`
            );
            const data = response.data || [];
            setAssignments(data);
            /*
             * Automatically select first assignment
             */
            if (data.length > 0) {
                setSelectedCourse(
                    String(data[0].CourseID)
                );
                setSelectedClass(
                    String(data[0].ClassID)
                );
                setSelectedSection(
                    data[0].SectionID !== null
                        ? String(data[0].SectionID)
                        : "all"
                );
            }

        } catch (error) {
            console.error(
                "Error loading class teacher assignments:",
                error
            );
            showSnackbar(
                "Unable to load class teacher assignments",
                "error"
            );
        } finally {
            setLoadingAssignments(false);
        }
    };
    /*
     * COURSES
     */
    const courses = useMemo(() => {
        const map = new Map();
        assignments.forEach(item => {
            if (!map.has(item.CourseID)) {

                map.set(
                    item.CourseID,
                    item
                );
            }
        });
        return Array.from(map.values());
    }, [assignments]);
    /*
     * CLASSES FOR SELECTED COURSE
     */
    const classes = useMemo(() => {
        const map = new Map();
        assignments
            .filter(
                item =>
                    String(item.CourseID) ===
                    String(selectedCourse)
            )
            .forEach(item => {
                if (!map.has(item.ClassID)) {

                    map.set(
                        item.ClassID,
                        item
                    );
                }
            });
        return Array.from(map.values());
    }, [
        assignments,
        selectedCourse
    ]);
    /*
     * SECTIONS FOR SELECTED COURSE + CLASS
     */
    const sections = useMemo(() => {
        const filtered =
            assignments.filter(item =>
                String(item.CourseID) ===
                    String(selectedCourse) &&
                String(item.ClassID) ===
                    String(selectedClass)
            );
        const map = new Map();
        filtered.forEach(item => {
            if (item.SectionID !== null) {
                if (!map.has(item.SectionID)) {
                    map.set(
                        item.SectionID,
                        item
                    );
                }
            }
        });
        return Array.from(map.values());
    }, [
        assignments,
        selectedCourse,
        selectedClass
    ]);
    /*
     * SELECTED ASSIGNMENT
     */
    const selectedAssignment = useMemo(() => {
        return assignments.find(item => {
            const courseMatch =
                String(item.CourseID) ===
                String(selectedCourse);
            const classMatch =
                String(item.ClassID) ===
                String(selectedClass);
            const itemSection =
                item.SectionID === null
                    ? "all"
                    : String(item.SectionID);
            return (
                courseMatch &&
                classMatch &&
                itemSection ===
                    String(selectedSection)
            );
        });
    }, [
        assignments,
        selectedCourse,
        selectedClass,
        selectedSection
    ]);
    /*
     * COURSE CHANGE
     */
    const handleCourseChange = (event) => {
        const value = event.target.value;
        setSelectedCourse(value);
        const firstClass =
            assignments.find(
                item =>
                    String(item.CourseID) ===
                    String(value)
            );
        if (firstClass) {
            setSelectedClass(
                String(firstClass.ClassID)
            );
            setSelectedSection(
                firstClass.SectionID !== null
                    ? String(firstClass.SectionID)
                    : "all"
            );
        } else {
            setSelectedClass("");
            setSelectedSection("");
        }
    };
    /*
     * CLASS CHANGE
     */
    const handleClassChange = (event) => {
        const value = event.target.value;
        setSelectedClass(value);
        const classAssignments =
            assignments.filter(item =>
                String(item.CourseID) ===
                    String(selectedCourse) &&
                String(item.ClassID) ===
                    String(value)
            );
        const first =
            classAssignments[0];
        if (first) {
            setSelectedSection(
                first.SectionID !== null
                    ? String(first.SectionID)
                    : "all"
            );
        } else {
            setSelectedSection("");
        }
    };
    /*
     * LOAD STUDENTS AUTOMATICALLY
     */
    useEffect(() => {
        if (
            !selectedCourse ||
            !selectedClass ||
            !selectedAssignment
        ) {
            setStudents([]);
            return;
        }
        loadStudents();
    }, [
        selectedCourse,
        selectedClass,
        selectedSection,
        selectedAssignment
    ]);
    const loadStudents = async () => {
        try {
            setLoadingStudents(true);
            const response = await axios.get(
                `${BASE_URL}/api/staff-class-students`,
                {
                    params: {
                        regId,
                        academicYearId:
                            selectedAssignment.AcademicYearID,
                        courseId:
                            selectedCourse,
                        classId:
                            selectedClass,
                        sectionId:
                            selectedSection
                    }
                }
            );
            setStudents(
                response.data || []
            );
        } catch (error) {

            console.error(
                "Error loading students:",
                error
            );
            setStudents([]);

            showSnackbar(
                error.response?.data?.message ||
                    "Unable to load students",
                "error"
            );
        } finally {

            setLoadingStudents(false);
        }
    };
    /*
     * FILTER STUDENTS
     */
    const filteredStudents = useMemo(() => {
        const search =
            searchText
                .trim()
                .toLowerCase();
        if (!search) {
            return students;
        }
        return students.filter(student => {
            return (
                String(
                    student.StudentName || ""
                )
                    .toLowerCase()
                    .includes(search)
                ||
                String(
                    student.ParentName || ""
                )
                    .toLowerCase()
                    .includes(search)
                ||
                String(
                    student.MobileNumber || ""
                )
                    .toLowerCase()
                    .includes(search)
                ||
                String(
                    student.RollNumber || ""
                )
                    .toLowerCase()
                    .includes(search)
                ||
                String(
                    student.SectionName || ""
                )
                    .toLowerCase()
                    .includes(search)
            );
        });
    }, [
        students,
        searchText
    ]);
    /*
     * OPEN PROGRESS REPORT
     */
    const handleStudentClick = async (student) => {
        setSelectedStudent(student);
        setProgress({
            attendance: "",
            homework: "",
            iq: "",
            classroomDiscipline: "",
            academicPerformance: "",
            behaviors: "",
            teachersRemark: ""
        });
        setProgressOpen(true);
        try {
            const response = await axios.get(
                `${BASE_URL}/api/progress-report`,
                {
                    params: {
                        studentId:
                            student.StudentID,
                        academicYearId:
                            student.AcademicYearID,
                        courseId:
                            student.CourseID,
                        classId:
                            student.ClassID,
                        sectionId:
                            student.SectionID
                    }
                }
            );
            if (response.data) {
                setProgress({
                    attendance:
                        response.data.Attendance || "",
                    homework:
                        response.data.Homework || "",
                    iq:
                        response.data.IQ || "",
                    classroomDiscipline:
                        response.data.ClassroomDiscipline || "",
                    academicPerformance:
                        response.data.AcademicPerformance || "",
                    behaviors:
                        response.data.Behaviors || "",
                    teachersRemark:
                        response.data.TeachersRemark || ""
                });
            }
        } catch (error) {
            console.error(
                "Error loading progress report:",
                error
            );
        }
    };
    /*
     * CHECKBOX SINGLE SELECTION
     */
    const handleProgressChange =
        (field, value) => {
            setProgress(prev => ({
                ...prev,
                [field]:
                    prev[field] === value
                        ? ""
                        : value
            }));
        };
    /*
     * SAVE PROGRESS REPORT
     */
    const handleSaveProgress = async () => {
        if (!selectedStudent) {
            return;
        }
        try {
            setSaving(true);
            await axios.post(
                `${BASE_URL}/api/progress-report`,
                {
                    studentId:
                        selectedStudent.StudentID,
                    staffRegId:
                        Number(regId),
                    academicYearId:
                        selectedStudent.AcademicYearID,
                    courseId:
                        selectedStudent.CourseID,
                    classId:
                        selectedStudent.ClassID,
                    sectionId:
                        selectedStudent.SectionID,
                    attendance:
                        progress.attendance,
                    homework:
                        progress.homework,
                    iq:
                        progress.iq,
                    classroomDiscipline:
                        progress.classroomDiscipline,
                    academicPerformance:
                        progress.academicPerformance,
                    behaviors:
                        progress.behaviors,
                    teachersRemark:
                        progress.teachersRemark
                }
            );
            showSnackbar(
                "Progress report saved successfully",
                "success"
            );
            setProgressOpen(false);
        } catch (error) {
            console.error(
                "Error saving progress report:",
                error
            );
            showSnackbar(
                error.response?.data?.message ||
                    "Unable to save progress report",
                "error"
            );
        } finally {
            setSaving(false);
        }
    };
    /*
     * SNACKBAR
     */
    const showSnackbar =
        (message, severity = "success") => {
            setSnackbar({
                open: true,
                message,
                severity
            });
        };
    /*
     * PROGRESS OPTION COMPONENT
     */
    const ProgressOptions = ({
        title,
        field,
        options
    }) => {
        return (
            <Box
                sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "#fafafa"
                }}
            >
                <Typography
                    fontWeight="bold"
                    sx={{
                        mb: 1
                    }}
                >
                    {title}
                </Typography>
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                >
                    {options.map(option => (
                        <Box
                            key={option}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: {
                                    xs: "100%",
                                    sm: "auto"
                                }
                            }}
                        >
                            <Checkbox
                                checked={
                                    progress[field] ===
                                    option
                                }
                                onChange={() =>
                                    handleProgressChange(
                                        field,
                                        option
                                    )
                                }
                            />
                            <Typography
                                variant="body2"
                            >
                                {option}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>
        );
    };
    return (
        <Box>
            {/* PAGE HEADER */}
            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg,#1976d2,#42a5f5)",
                    color: "white"
                }}
            >
                <CardContent>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                    >
                        <Avatar
                            sx={{
                                width: 55,
                                height: 55,
                                backgroundColor:
                                    "rgba(255,255,255,.2)"
                            }}
                        >
                            <School />
                        </Avatar>
                        <Box>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                            >
                                Class Students
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    opacity: 0.9
                                }}
                            >
                                Manage students and
                                prepare progress reports
                            </Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
            {/* FILTER CARD */}
            <Paper
                elevation={2}
                sx={{
                    p: {
                        xs: 2,
                        md: 3
                    },
                    mb: 3,
                    borderRadius: 3
                }}
            >
                <Grid
                    container
                    spacing={2}
                >
                    {/* COURSE */}
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <FormControl
                            fullWidth
                            size="small"
                        >
                            <InputLabel>
                                Course
                            </InputLabel>
                            <Select
                                value={
                                    selectedCourse
                                }
                                label="Course"
                                onChange={
                                    handleCourseChange
                                }
                            >

                                {courses.map(
                                    course => (
                                        <MenuItem
                                            key={
                                                course.CourseID
                                            }
                                            value={
                                                String(
                                                    course.CourseID
                                                )
                                            }
                                        >
                                            {
                                                course.CourseName
                                            }
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* CLASS */}
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <FormControl
                            fullWidth
                            size="small"
                            disabled={
                                !selectedCourse
                            }
                        >
                            <InputLabel>
                                Class
                            </InputLabel>
                            <Select
                                value={
                                    selectedClass
                                }
                                label="Class"
                                onChange={
                                    handleClassChange
                                }
                            >
                                {classes.map(
                                    item => (
                                        <MenuItem
                                            key={
                                                item.ClassID
                                            }
                                            value={
                                                String(
                                                    item.ClassID
                                                )
                                            }
                                        >
                                            {
                                                item.ClassName
                                            }
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* SECTION */}
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <FormControl
                            fullWidth
                            size="small"
                            disabled={
                                !selectedClass ||
                                sections.length === 0
                            }
                        >
                            <InputLabel>
                                Section
                            </InputLabel>
                            <Select
                                value={
                                    selectedSection
                                }
                                label="Section"
                                onChange={event =>
                                    setSelectedSection(
                                        event.target.value
                                    )
                                }
                            >
                                {sections.length > 0 && (
                                    sections.map(
                                        item => (
                                            <MenuItem
                                                key={
                                                    item.SectionID
                                                }
                                                value={
                                                    String(
                                                        item.SectionID
                                                    )
                                                }
                                            >
                                                {
                                                    item.SectionName
                                                }
                                            </MenuItem>
                                        )
                                    )
                                )}
                                {sections.length === 0 && (
                                    <MenuItem value="all">
                                        All Sections
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>
            {/* SEARCH */}
            <Paper
                elevation={1}
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 3
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search student, parent, roll number, mobile or section..."
                    value={searchText}
                    onChange={event =>
                        setSearchText(
                            event.target.value
                        )
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position="start"
                            >
                                <Search />
                            </InputAdornment>
                        )
                    }}
                />
            </Paper>
            {/* STUDENT COUNT */}
            <Box
                sx={{
                    mb: 1.5,
                    display: "flex",
                    justifyContent:
                        "space-between",
                    alignItems: "center"
                }}
            >
                <Typography
                    fontWeight="bold"
                >
                    Students
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {filteredStudents.length}
                    {" "}student(s)
                </Typography>
            </Box>
            {/* STUDENT TABLE */}
            <TableContainer
                component={Paper}
                elevation={2}
                sx={{
                    borderRadius: 3,
                    maxHeight: "65vh"
                }}
            >
                <Table
                    stickyHeader
                    size="small"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                R.No
                            </TableCell>

                            <TableCell
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                Student Name
                            </TableCell>

                            <TableCell
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                Parent Name
                            </TableCell>

                            <TableCell
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                Class
                            </TableCell>

                            <TableCell
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                Section
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: "bold"
                                }}
                            >
                                Mobile
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingStudents ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    align="center"
                                    sx={{
                                        py: 6
                                    }}
                                >
                                    <CircularProgress />
                                    <Typography
                                        sx={{
                                            mt: 1
                                        }}
                                        variant="body2"
                                    >
                                        Loading students...
                                    </Typography>

                                </TableCell>
                            </TableRow>
                        ) : filteredStudents.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    align="center"
                                    sx={{
                                        py: 6
                                    }}
                                >
                                    <Person
                                        sx={{
                                            fontSize: 45,
                                            color: "text.disabled"
                                        }}
                                    />
                                    <Typography
                                        color="text.secondary"
                                    >
                                        No students found
                                    </Typography>
                                </TableCell>
                            </TableRow>

                        ) : (
                            filteredStudents.map(
                                student => (
                                    <TableRow
                                        key={
                                            student.StudentID
                                        }
                                        hover
                                    >
                                        <TableCell>
                                            {
                                                student.RollNumber ||
                                                "-"
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="text"
                                                onClick={() =>
                                                    handleStudentClick(
                                                        student
                                                    )
                                                }
                                                sx={{
                                                    textTransform:
                                                        "none",
                                                    fontWeight:
                                                        "bold"
                                                }}
                                            >
                                                {
                                                    student.StudentName
                                                }
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            {
                                                student.ParentName ||
                                                "-"
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {
                                                student.ClassName
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {
                                                student.SectionName ||
                                                "-"
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {
                                                student.MobileNumber
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* PROGRESS REPORT MODAL */}
            <Dialog
                open={progressOpen}
                onClose={() =>
                    !saving &&
                    setProgressOpen(false)
                }
                fullWidth
                maxWidth="md"
                fullScreen={
                    window.innerWidth < 600
                }
            >
                <DialogTitle
                    sx={{
                        background:
                            "linear-gradient(135deg,#1976d2,#42a5f5)",
                        color: "white",
                        py: 2
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                            >
                                Student Progress Report
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    opacity: 0.9
                                }}
                            >
                                Fill the student's
                                progress details
                            </Typography>
                        </Box>
                        <IconButton
                            color="inherit"
                            onClick={() =>
                                !saving &&
                                setProgressOpen(false)
                            }
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        p: {
                            xs: 2,
                            md: 3
                        },
                        backgroundColor:
                            "#f7f9fc"
                    }}
                >
                    {selectedStudent && (
                        <>
                            {/* STUDENT INFORMATION */}
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    borderRadius: 3
                                }}
                            >
                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Student Name
                                        </Typography>
                                        <Typography
                                            fontWeight="bold"
                                        >
                                            {
                                                selectedStudent.StudentName
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Father Name
                                        </Typography>
                                        <Typography
                                            fontWeight="bold"
                                        >
                                            {
                                                selectedStudent.ParentName ||
                                                "-"
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={6}
                                        md={3}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            R.No
                                        </Typography>

                                        <Typography
                                            fontWeight="bold"
                                        >
                                            {
                                                selectedStudent.RollNumber ||
                                                "-"
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={6}
                                        md={5}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Class / Section
                                        </Typography>
                                        <Typography
                                            fontWeight="bold"
                                        >
                                            {
                                                selectedStudent.ClassName
                                            }

                                            {" / "}

                                            {
                                                selectedStudent.SectionName ||
                                                "-"
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={4}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Mobile
                                        </Typography>
                                        <Typography
                                            fontWeight="bold"
                                        >
                                            {
                                                selectedStudent.MobileNumber
                                            }
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                            {/* PROGRESS OPTIONS */}
                            <ProgressOptions
                                title="1) Attendance"
                                field="attendance"
                                options={[
                                    "Regular",
                                    "Irregular"
                                ]}
                            />
                            <ProgressOptions
                                title="2) Homework"
                                field="homework"
                                options={[
                                    "Complete",
                                    "Incomplete"
                                ]}
                            />
                            <ProgressOptions
                                title="3) IQ"
                                field="iq"
                                options={[
                                    "Excellent",
                                    "Moderate",
                                    "Poor"
                                ]}
                            />
                            <ProgressOptions
                                title="4) Classroom Discipline"
                                field="classroomDiscipline"
                                options={[
                                    "Best",
                                    "Better",
                                    "Good",
                                    "Not good"
                                ]}
                            />
                            <ProgressOptions
                                title="5) Academic Performance"
                                field="academicPerformance"
                                options={[
                                    "Above average",
                                    "Average",
                                    "Below Average"
                                ]}
                            />
                            <ProgressOptions
                                title="6) Behaviors"
                                field="behaviors"
                                options={[
                                    "Disciplined",
                                    "Attentive",
                                    "Punctual",
                                    "Obedient",
                                    "Respectful",
                                    "Honest",
                                    "Submissive",
                                    "Co-operative",
                                    "Indiscipline",
                                    "Disobedient",
                                    "Impolite",
                                    "Rude",
                                    "Aggressive",
                                    "Naughty"
                                ]}
                            />
                            <ProgressOptions
                                title="7) Teachers Remark"
                                field="teachersRemark"
                                options={[
                                    "Outstanding",
                                    "Excellent",
                                    "Very Good",
                                    "Good",
                                    "Above Average",
                                    "Average",
                                    "Should Improve"
                                ]}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        p: 2,
                        borderTop:
                            "1px solid #e0e0e0"
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() =>
                            setProgressOpen(false)
                        }
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            saving
                                ? <CircularProgress
                                    size={18}
                                    color="inherit"
                                  />
                                : <Save />
                        }
                        onClick={
                            handleSaveProgress
                        }
                        disabled={saving}
                    >
                        {saving
                            ? "Saving..."
                            : "Save Progress Report"}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* SNACKBAR */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() =>
                    setSnackbar(prev => ({
                        ...prev,
                        open: false
                    }))
                }
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right"
                }}
            >
                <Alert
                    severity={
                        snackbar.severity
                    }
                    variant="filled"
                    onClose={() =>
                        setSnackbar(prev => ({
                            ...prev,
                            open: false
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}